
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Send, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { CastResponses } from "./CastResponses";
import type { Cast } from "@/hooks/useCasts";

interface CastResponseModalProps {
  cast: Cast & { solved?: boolean };
  onClose: () => void;
}

export const CastResponseModal = ({ cast, onClose }: CastResponseModalProps) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <MessageCircle className="text-iso-solid" size={20} />
            <h2 className="text-xl font-bold text-ink-primary">ISO Cast Responses</h2>
            {cast.solved && (
              <Badge className="px-2 py-0.5 text-xs rounded-full border-iso-soft-border bg-iso-soft-bg text-[#3E1C8A]">
                Solved
              </Badge>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Cast Summary */}
        <div className="p-4 bg-iso-soft-bg border-b border-iso-soft-border">
          <div className="flex items-start space-x-3">
            <Avatar className="w-10 h-10">
              <AvatarImage src={cast.user.avatar} alt={cast.user.name} />
              <AvatarFallback>{cast.user.name[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h3 className="font-semibold text-ink-primary">{cast.user.name}</h3>
                <span className="text-ink-meta text-sm">@{cast.user.username}</span>
              </div>
              <div className="mt-2">
                <h4 className="font-medium text-ink-primary text-sm">Looking for:</h4>
                <p className={`text-ink-body text-sm ${cast.solved ? 'line-through opacity-75' : ''}`}>
                  {cast.iso_request}
                </p>
                {cast.solved && (
                  <p className="text-[#3E1C8A] text-xs mt-1 font-medium">✓ This request has been solved</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Responses */}
        <div className="overflow-y-auto max-h-[50vh] p-4">
          <CastResponses castId={cast.id} hideAddResponse={cast.solved} />
        </div>
      </div>
    </div>,
    document.body
  );
};
